import { useMemo, useEffect } from "react";
import { Select } from "antd";
import { IconArrowDown } from "@icons/index";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { TestId } from "@shared/testIds";
import { setDescParams, selectVideoCreatingParams, selectCreateOptions } from "../videoCreatingSlice";
import { Trans } from "react-i18next";
import { VideoCreationRequestInternalModelId } from "@apis/generated/openapi";

interface ModelSelectProps {
  disabled?: boolean;
}

const ModelSelect = ({ disabled = false }: ModelSelectProps) => {
  const dispatch = useAppDispatch();
  const params = useAppSelector(selectVideoCreatingParams);
  const { duration, internalModelId } = params.descParams;
  const createOptions = useAppSelector(selectCreateOptions);
  const { modelOptions, rules } = createOptions;

  const allowedModelValues = rules?.duration_model?.[duration];

  const filteredOptions = useMemo(() => {
    if (!allowedModelValues?.length) return [];
    return modelOptions.filter((opt) => allowedModelValues.includes(opt.value));
  }, [allowedModelValues, modelOptions]);

  const selectOptions = filteredOptions.map((opt) => ({ value: opt.value, label: <Trans i18nKey={opt.label} /> }));
  const hasOptions = selectOptions.length > 0;
  const visible = selectOptions.length > 1;

  useEffect(() => {
    if (!hasOptions) return;
    const supportedValues = filteredOptions.map((o) => o.value);
    if (!internalModelId || !supportedValues.includes(internalModelId)) {
      const firstModelId = filteredOptions[0].value;
      dispatch(setDescParams({ internalModelId: firstModelId as VideoCreationRequestInternalModelId }));
    }
  }, [hasOptions, internalModelId, filteredOptions, dispatch]);

  if (!visible) return null;

  return (
    <Select
      size="large"
      disabled={disabled}
      data-testid={TestId.VideoModelSelect}
      value={
        internalModelId && filteredOptions.some((o) => o.value === internalModelId)
          ? internalModelId
          : filteredOptions[0]?.value
      }
      onChange={(v) => dispatch(setDescParams({ internalModelId: v as VideoCreationRequestInternalModelId }))}
      options={selectOptions}
      className="param-select w-[140px]"
      popupMatchSelectWidth={false}
      suffixIcon={<IconArrowDown className="w-[16px] h-[16px] text-kds-fg-subtle" />}
      optionRender={(option) => {
        const { label } = option;
        const desc = filteredOptions.find((o) => o.value === option.value)?.description;
        return desc ? (
          <div className="flex flex-col leading-tight max-w-[200px]">
            <div>
              <Trans i18nKey={label as unknown as string} />
            </div>
            <div className="text-xs text-kds-fg-subtle whitespace-normal">
              <Trans i18nKey={desc} />
            </div>
          </div>
        ) : (
          option.label
        );
      }}
    />
  );
};

export default ModelSelect;
